import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

/**
 * Value types a filter key can hold in the URL query string.
 * A filter schema maps each supported key to one of these.
 */
export const FilterType = {
  STRING: 'string',
  NUMBER: 'number',
  STRING_ARRAY: 'stringArray',
  NUMBER_ARRAY: 'numberArray',
};

/**
 * Reports whether a filter type holds many values.
 * @param {string} type - One of FilterType.
 * @returns {boolean} True for array types.
 */
const isArrayType = (type) =>
  type === FilterType.STRING_ARRAY || type === FilterType.NUMBER_ARRAY;

/**
 * Reads one filter key out of a query string according to its declared type.
 * Array types are stored as repeated keys (`meterIds=A&meterIds=B`) so that
 * values containing separators survive a round trip.
 * @param {URLSearchParams} params - Query string being read.
 * @param {string} key - Filter key to read.
 * @param {string} type - One of FilterType.
 * @returns {string|number|Array|undefined} Decoded value, or undefined when absent or unusable.
 */
export const decodeFilter = (params, key, type) => {
  if (isArrayType(type)) {
    const raw = params.getAll(key).filter((value) => value !== '');
    const values =
      type === FilterType.NUMBER_ARRAY
        ? raw.map(Number).filter(Number.isFinite)
        : raw;
    return values.length ? values : undefined;
  }

  const raw = params.get(key);
  if (raw === null || raw === '') return undefined;

  if (type === FilterType.NUMBER) {
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  }

  return raw;
};

/**
 * Writes one filter key into a query string, removing it when the value is empty.
 * Mutates the supplied params.
 * @param {URLSearchParams} params - Query string being written to.
 * @param {string} key - Filter key to write.
 * @param {string} type - One of FilterType.
 * @param {string|number|Array|undefined} value - Value to store.
 * @returns {void}
 */
export const encodeFilter = (params, key, type, value) => {
  params.delete(key);

  if (value === undefined || value === null || value === '') return;

  if (isArrayType(type)) {
    const values = Array.isArray(value) ? value : [value];
    values
      .filter((item) => item !== undefined && item !== null && item !== '')
      .forEach((item) => params.append(key, String(item)));
    return;
  }

  params.set(key, String(value));
};

/**
 * Keeps a declared set of filters in the URL query string.
 *
 * Only keys present in the schema are treated as filters. Anything else in the
 * query string — navigation state such as the active tab — is left untouched by
 * both updates and clears.
 *
 * @param {Object<string, string>} schema - Map of filter key to FilterType. Must be a
 *   stable reference (declare it at module level) so the returned values stay memoised.
 * @returns {{filters: object, updateFilters: Function, clearFilters: Function}} Current
 *   filter values, a partial-update function, and a function that clears filters only.
 */
export const useURLFilters = (schema) => {
  const location = useLocation();
  const history = useHistory();

  const filters = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return Object.entries(schema).reduce((result, [key, type]) => {
      const value = decodeFilter(params, key, type);
      if (value !== undefined) result[key] = value;
      return result;
    }, {});
  }, [location.search, schema]);

  const updateFilters = useCallback(
    (changes) => {
      const params = new URLSearchParams(location.search);

      Object.entries(changes).forEach(([key, value]) => {
        if (!(key in schema)) return;
        encodeFilter(params, key, schema[key], value);
      });

      history.push({ search: params.toString() });
    },
    [location.search, history, schema]
  );

  const clearFilters = useCallback(() => {
    const params = new URLSearchParams(location.search);
    Object.keys(schema).forEach((key) => params.delete(key));
    history.push({ search: params.toString() });
  }, [location.search, history, schema]);

  return { filters, updateFilters, clearFilters };
};
